import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import {
	dateTimeColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { concatWithPipe } from "src/utils/strings.tsx";
import { getShopOrderOperationStateLabel } from "src/api/generated/erp/db/types/enums/shopOrderOperationState.ts";
import { getPurchaseOrderStateLabel } from "src/api/generated/erp/db/types/enums/purchaseOrderState.ts";
import { OpenPurchaseOrderButton } from "src/components/views/erp/utilComponents/OpenPurchaseOrderButton.tsx";
import { ShopOrderOperationView } from "src/api/generated/erp/db/types/tables/shopOrderOperationView.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { faAngleDown, faAngleUp, faCheck, faPlay, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { ShopOrderOperationEditApi } from "src/api/generated/erp/production/api/shopOrderOperation/shopOrderOperationEditApi.ts";
import { ProductionJobQueueOperationActionApi } from "src/api/generated/erp/production/jobQueues/api/productionJobQueueOperationActionApi.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { ImportDocumentsToShopOrderOperationsButton } from "src/components/views/erp/production/shopOrderOperation/ImportDocumentsToShopOrderOperationsButton.tsx";
import { ShopOrderOperationForm } from "src/components/views/erp/production/shopOrderOperation/ShopOrderOperationForm.tsx";
import { ShopOrderOperationDataGridApi } from "src/api/generated/erp/production/api/shopOrderOperation/shopOrderOperationDataGridApi.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ShopOrderOperationsDataGridProps {
	shopOrderId: number;
}

export const ShopOrderOperationsDataGrid = ({ shopOrderId }: ShopOrderOperationsDataGridProps) => {
	const hasPermission = useHasPermission();
	const editingEnabled = hasPermission(Permissions.Production.ShopOrders.MANAGE);

	return (
		<ClientSideDataGridModel
			gridId={"EE371799EFA8D206"}
			fetchData={async ({ searchQuery }) =>
				ShopOrderOperationDataGridApi.getData({
					searchQuery: searchQuery,
					shopOrderId: shopOrderId,
				})
			}
			getRows={(data) => data.shopOrderOperations}
			getRowId={(row) => row.shopOrderOperationId}
			customFunctionUiActionView={"SHOP_ORDER_OPERATIONS"}
			initialParams={{
				shopOrderId: shopOrderId,
				searchQuery: "",
			}}
			render={({ data: { shopOrder }, dataGridProps, onlySelectedRow, refreshData }) => {
				const shopOrderIsClosed =
					shopOrder.shopOrderState === "READY" || shopOrder.shopOrderState === "CANCELLED";
				return (
					<FormCrudDataGrid<ShopOrderOperationView>
						columns={[
							integerColumn({
								field: "position",
								headerName: i18n.t("number_short"),
								width: 50,
							}),
							textColumn({
								field: "operationId",
								headerName: i18n.t("operation"),
								width: 150,
								valueGetter: (_, row) => concatWithPipe(row.operationNo, row.operationDescription),
							}),
							dateTimeColumn({
								field: "actualBeginDate",
								headerName: i18n.t("actual_begin_date"),
								width: 150,
							}),
							dateTimeColumn({
								field: "actualEndDate",
								headerName: i18n.t("actual_end_date"),
								width: 150,
							}),
							textColumn({
								field: "operator",
								headerName: i18n.t("shop_order_operation.operator"),
							}),
							textColumn({
								field: "productionLineName",
								headerName: i18n.t("production_line"),
							}),
							textColumn({
								field: "shopOrderOperationState",
								headerName: i18n.t("state"),
								valueGetter: (_, row) => {
									const stateLabel = getShopOrderOperationStateLabel(row.shopOrderOperationState);
									if (row.subcontractingPurchaseOrderId == null) return stateLabel;
									const poStateLabel =
										row.subcontractingPurchaseOrderState != null ?
											getPurchaseOrderStateLabel(row.subcontractingPurchaseOrderState)
										:	null;
									return `${stateLabel} (${[row.subcontractingPurchaseOrderId, poStateLabel]
										.filter((value) => value != null && value !== "")
										.join(", ")})`;
								},
							}),
							textColumn({
								field: "materialOperationId",
								headerName: i18n.t("material_operation"),
								width: 150,
								valueGetter: (_, row) =>
									concatWithPipe(row.materialOperationNo, row.materialOperationDescription),
							}),
							floatColumn({
								field: "setUpTime",
								headerName: i18n.t("set_up_time"),
							}),
							floatColumn({
								field: "workingTime",
								headerName: i18n.t("working_time"),
							}),
							moneyColumn({
								field: "workCost",
								headerName: i18n.t("work_cost"),
							}),
							integerColumn({
								field: "shopOrderOperationId",
								headerName: i18n.t("id"),
							}),
						]}
						actionBarComponents={
							<>
								<DocumentsOfObjectButton
									objectRef={nullableAavoObjectRef(
										"SHOP_ORDER_OPERATION",
										onlySelectedRow?.shopOrderOperationId,
									)}
									editable={editingEnabled}
								/>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={(value) => refreshData({ searchQuery: value })}
								/>
								{editingEnabled && (
									<AsyncButton
										icon={faAngleUp}
										disabled={!onlySelectedRow}
										onClick={async () => {
											await ShopOrderOperationEditApi.movePosition({
												shopOrderOperationId: onlySelectedRow!.shopOrderOperationId,
												direction: "UP",
											});
											await refreshData();
										}}
									/>
								)}
								{editingEnabled && (
									<AsyncButton
										icon={faAngleDown}
										disabled={!onlySelectedRow}
										onClick={async () => {
											await ShopOrderOperationEditApi.movePosition({
												shopOrderOperationId: onlySelectedRow!.shopOrderOperationId,
												direction: "DOWN",
											});
											await refreshData();
										}}
									/>
								)}
							</>
						}
						actionBarMenuComponents={[
							editingEnabled && <ImportDocumentsToShopOrderOperationsButton shopOrderId={shopOrderId} />,
						]}
						rowContextMenuComponents={({ allSelectedRows, row, onlySingleRowSelected }) => {
							const released =
								editingEnabled ?
									allSelectedRows.filter((r) => r.shopOrderOperationState === "RELEASED")
								:	[];
							const started =
								editingEnabled ?
									allSelectedRows.filter((r) => r.shopOrderOperationState === "STARTED")
								:	[];
							const ready =
								editingEnabled ?
									allSelectedRows.filter((r) => r.shopOrderOperationState === "READY")
								:	[];
							return [
								released.length > 0 && (
									<AsyncMenuButton
										key="start"
										label={i18n.t("start")}
										icon={faPlay}
										onClick={async () => {
											await ProductionJobQueueOperationActionApi.startOperations({
												shopOrderOperationIds: released.map((r) => r.shopOrderOperationId),
											});
											await refreshData();
										}}
									/>
								),
								started.length > 0 && (
									<AsyncMenuButton
										key="revertStart"
										label={i18n.t("revert_start")}
										icon={faUndo}
										onClick={async () => {
											await ProductionJobQueueOperationActionApi.revertStartOperations({
												shopOrderOperationIds: started.map((r) => r.shopOrderOperationId),
											});
											await refreshData();
										}}
									/>
								),
								started.length > 0 && (
									<AsyncMenuButton
										key="complete"
										label={i18n.t("complete")}
										icon={faCheck}
										onClick={async () => {
											await ProductionJobQueueOperationActionApi.completeOperations({
												shopOrderOperationIds: started.map((r) => r.shopOrderOperationId),
											});
											await refreshData();
										}}
									/>
								),
								ready.length > 0 && (
									<AsyncMenuButton
										key="revertComplete"
										label={i18n.t("revert_complete")}
										icon={faUndo}
										onClick={async () => {
											await ProductionJobQueueOperationActionApi.revertCompleteOperations({
												shopOrderOperationIds: ready.map((r) => r.shopOrderOperationId),
											});
											await refreshData();
										}}
									/>
								),
								onlySingleRowSelected && (
									<OpenPurchaseOrderButton
										key="openPurchaseOrder"
										purchaseOrderId={row.subcontractingPurchaseOrderId ?? undefined}
									/>
								),
							];
						}}
						remove={{
							type: editingEnabled ? "enabled" : "disabled",
							action: async (params) => {
								await ShopOrderOperationEditApi.delete_({
									shopOrderOperations: params.items,
								});
							},
						}}
						form={{
							addRowEnabled: editingEnabled && !shopOrderIsClosed,
							editEnabled: editingEnabled,
							dialogSize: "sm",
							dialogTitle: i18n.t("shop_order_operation"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.production.shopOrderOperation.formDialog"),
								title: i18n.t("shop_order_operation"),
								data: {
									shopOrderId,
									shopOrderOperationId: row?.shopOrderOperationId ?? null,
									operationId: row?.operationId ?? null,
								},
							}),
							component: ({ row, ...other }) => (
								<ShopOrderOperationForm
									shopOrderId={shopOrderId}
									shopOrderOperationId={row?.shopOrderOperationId ?? null}
									{...other}
								/>
							),
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.production.shopOrderOperations.grid"),
							data: { shopOrderId },
							rowFields: ["shopOrderOperationId", "shopOrderId", "operationId", "productionLineId"],
							selectedRowPathSegment: (row) => `${i18n.t("operation")} ${row.operationNo}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
};
